import React, { forwardRef } from 'react';
import { tv } from 'tailwind-variants';

import 'components/partials/button/button.css';

export type ButtonVariant =
  | 'brand-filled'
  | 'brand-outlined'
  | 'brand-invert'
  | 'brand-text'
  | 'brand-text-link'
  | 'highlight-filled'
  | 'highlight-outlined'
  | 'highlight-invert'
  | 'highlight-text'
  | 'highlight-text-link'
  | 'reserve-filled'
  | 'reserve-outlined'
  | 'reserve-invert'
  | 'reserve-text'
  | 'reserve-text-link'
  | 'neutral-filled'
  | 'neutral-outlined'
  | 'neutral-invert'
  | 'neutral-text'
  | 'neutral-text-link'
  | 'critical-filled'
  | 'critical-outlined'
  | 'critical-invert'
  | 'critical-text'
  | 'critical-text-link';

export type ButtonSize = 'baby' | 'xs' | 'sm' | 'md' | 'lg';

const buttonVariants = tv({
  base: 'btn',
  variants: {
    variant: {
      'brand-filled': 'btn-variant-brand-filled',
      'brand-outlined': 'btn-variant-brand-outlined',
      'brand-invert': 'btn-variant-brand-invert',
      'brand-text': 'btn-variant-brand-text',
      'brand-text-link': 'btn-variant-brand-text-link',
      'highlight-filled': 'btn-variant-highlight-filled',
      'highlight-outlined': 'btn-variant-highlight-outlined',
      'highlight-invert': 'btn-variant-highlight-invert',
      'highlight-text': 'btn-variant-highlight-text',
      'highlight-text-link': 'btn-variant-highlight-text-link',
      'reserve-filled': 'btn-variant-reserve-filled',
      'reserve-outlined': 'btn-variant-reserve-outlined',
      'reserve-invert': 'btn-variant-reserve-invert',
      'reserve-text': 'btn-variant-reserve-text',
      'reserve-text-link': 'btn-variant-reserve-text-link',
      'neutral-filled': 'btn-variant-neutral-filled',
      'neutral-outlined': 'btn-variant-neutral-outlined',
      'neutral-invert': 'btn-variant-neutral-invert',
      'neutral-text': 'btn-variant-neutral-text',
      'neutral-text-link': 'btn-variant-neutral-text-link',
      'critical-filled': 'btn-variant-critical-filled',
      'critical-outlined': 'btn-variant-critical-outlined',
      'critical-invert': 'btn-variant-critical-invert',
      'critical-text': 'btn-variant-critical-text',
      'critical-text-link': 'btn-variant-critical-text-link',
    },
    size: {
      baby: 'btn-size-baby',
      xs: 'btn-size-xs',
      sm: 'btn-size-sm',
      md: 'btn-size-md',
      lg: 'btn-size-lg',
    },
  },
  defaultVariants: {
    variant: 'reserve-filled',
    size: 'md',
  },
});

const ICON_SIZE: Record<ButtonSize, number> = {
  baby: 12,
  xs: 16,
  sm: 16,
  md: 20,
  lg: 20,
};

const SPINNER_SIZE: Record<ButtonSize, string> = {
  baby: 'w-3 h-3',
  xs: 'w-4 h-4',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-5 h-5',
};

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconBefore?: React.ReactNode;
  iconAfter?: React.ReactNode;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  'data-testid'?: string;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'reserve-filled',
      size = 'md',
      iconBefore,
      iconAfter,
      loading = false,
      onClick,
      disabled,
      children,
      type,
      form,
      className,
      'data-testid': dataTestId = 'custom-button',
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const iconSize = ICON_SIZE[size] || 20;

    return (
      <button
        className={buttonVariants({ variant, size, className })}
        onClick={onClick}
        disabled={isDisabled}
        ref={ref}
        type={type}
        form={form}
        data-testid={dataTestId}
        {...rest}
      >
        {loading ? (
          <span
            className={`inline-block animate-spin border-2 border-current border-t-transparent rounded-full ${SPINNER_SIZE[size]}`}
          />
        ) : (
          iconBefore && (
            <span
              className="inline-flex items-center justify-center shrink-0"
              style={{ width: iconSize, height: iconSize }}
            >
              {iconBefore}
            </span>
          )
        )}
        {children}
        {iconAfter && (
          <span
            className="inline-flex items-center justify-center shrink-0"
            style={{ width: iconSize, height: iconSize }}
          >
            {iconAfter}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { buttonVariants };
export default Button;
